import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import Button from '../../components/buttons/Button';
import Image from '../../components/images/Image';
import Shimmer from './components/ViewAdminDetailsShimmer';
import useApi from '../../services/useApi';
import { toTitleCase } from '../../components/commonfunctions/TitleCase';
import useTranslation from '../../components/CustomHooks/useTranslation';
import { adminContents } from './components/AdminManagementContent';
import config from '../../config/config';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirefoxCssFix } from '../../components/commonfunctions/getFirefoxCssFix';
import CustomModal from '../../components/CustomModal';
import { useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { showToast } from '../../components/toastMessage/toastSlice';
import { useSelector } from 'react-redux';
import { setUserCode, setUserName } from '../../store/slices/activeListSlice';

type PersonalDetails = {
  user_role: string;
  official_email_address: string;
  phone_number: string;
};

type ResidentialDetails = {
  address: string;
};

type AdminDetails = {
  personal_details: PersonalDetails;
  residential_details: ResidentialDetails;
};

type viewDetails = {
  id: string;
  user_code: string;
  ussd_code: string;
  name: string;
  personal_details: PersonalDetails;
  residential_details: ResidentialDetails;
  access_privileges: {
    [key: string]: string[];
  };
  status?: boolean;
  sop?: string;
  wallet_access?: string;
};

export default function ViewAdminDetails() {
  const { getAPI, PatchAPI } = useApi();
  const { translate, notEnglish, replace } = useTranslation(adminContents);
  const [selectedAction, setSelectedAction] = useState('');
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const navigate = useNavigate();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const pathname = useSelector(
    (state: RootState) => state.geolocation.previousUrl
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsRef.current &&
        !detailsRef.current.contains(event.target as Node)
      ) {
        detailsRef.current.open = false;
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const [adminDetails, setAdminDetails] = useState<viewDetails>({
    id: '',
    user_code: '',
    ussd_code: '',
    name: '',
    personal_details: {
      user_role: '',
      official_email_address: '',
      phone_number: '',
    },
    residential_details: {
      address: '',
    },
    access_privileges: {},
    sop: '',
  });

  const [information, setInformation] = useState<AdminDetails>({
    personal_details: {
      user_role: '',
      official_email_address: '',
      phone_number: '',
    },
    residential_details: {
      address: '',
    },
  });

  const fetchAdminDetails = async () => {
    setLoading(true);
    try {
      const response = await getAPI(`admins/${id}`);
      if (!response?.error) {
        const temp = response?.data?.data;
        delete temp['created_at'];
        delete temp['updated_at'];
        setAdminDetails(temp);
        setInformation({
          personal_details: temp?.personal_details,
          residential_details: temp?.residential_details,
        });
        dispatch(setUserCode(temp?.user_code ? temp?.user_code : ''));
        dispatch(
          setUserName(
            temp?.name || '-'
          )
        );
      } else {
        navigate('/not-found', {
          state: { route: '/admin-management/list-of-admins' },
        });
      }
    } catch (error) {
      console.error('Error fetching admin details:', error);
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    fetchAdminDetails();
  }, []);

  const handleStatus = async (text?: string) => {
    setButtonLoading(true);
    try {
      let payload: {
        status?: boolean;
        extras?: { deactivate_reason: string };
      } = { status: true };
      let endPoint = `admins/${id}`;
      let message = '';
      switch (selectedAction) {
        case 'activate':
          payload = { status: true };
          message = translate('Admin activated successfully');
          break;
        case 'deactivate':
          payload = {
            status: false,
            extras: { deactivate_reason: text || '' },
          };
          message = translate('Admin deactivated successfully');
          break;
        case 'password':
          endPoint = `admins/reset-password/${id}`;
          payload = {};
          message = `New password has been sent to ${adminDetails.name} (${adminDetails.user_code}) via email successfully.`;
          break;
        case 'PIN':
          endPoint = `admins/reset-pin/${id}`;
          payload = {};
          message = `New PIN has been sent to ${adminDetails.name} (${adminDetails.user_code}) via email successfully.`;
          break;
        default:
          break;
      }

      const res = await PatchAPI(endPoint, payload);

      if (!res.error) {
        dispatch(
          showToast({
            type: 'success',
            message: message,
          })
        );
        fetchAdminDetails();
      } else {
        dispatch(
          showToast({
            type: 'error',
            message: res.data.message,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsOpen(false);
      setButtonLoading(false);
    }
  };

  const resetOptions = ['password', 'PIN'];
  const handelActions = (option: string) => {
    setSelectedAction(option);
    setIsOpen(true);
  };
  const getModelText = () => {
    switch (selectedAction) {
      case 'activate':
        return {
          title: translate('Confirm Activation'),
          description: translate(
            'This action will grant access to features and tasks'
          ),
          buttonName: translate('Activate'),
        };
      case 'deactivate':
        return {
          title: translate('Confirm Deactivation'),
          description: translate(
            'This action will revoke access to all features and tasks'
          ),
          buttonName: translate('Deactivate'),
        };
      case 'password':
        return {
          title: translate('Reset Password'),
          description: replace(
            `Would you like to proceed with resetting the password for {{admin_name}} ({{admin_user_code}})?`,
            {
              admin_name: adminDetails.name,
              admin_user_code: adminDetails.user_code,
            }
          ),
          buttonName: translate('Reset'),
        };
      case 'PIN':
        return {
          title: translate('Reset PIN'),
          description: replace(
            `Would you like to proceed with resetting the PIN for {{admin_name}} ({{admin_user_code}})?`,
            {
              admin_name: adminDetails.name,
              admin_user_code: adminDetails.user_code,
            }
          ),
          buttonName: translate('Reset'),
        };
      default:
        return {};
    }
  };
  return (
    <div dir={notEnglish ? 'rtl' : 'ltr'}>
      <Breadcrumbs
        navPath={['Admin Management', 'List of Admins', 'View Admin Profile']}
        links={
          pathname
            ? [pathname, pathname]
            : [
                '/admin-management/list-of-admins',
                '/admin-management/list-of-admins',
              ]
        }
        heading="View Admin Profile"
      />

      {!loading ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="px-6 py-5 border-b border-[#E5E9EB] flex justify-between items-center">
            <div>
              <h1
                title={adminDetails?.name}
                data-testid="user-name"
                className="text-heading2"
              >
                {adminDetails?.name || '-'}
              </h1>
              <p
                title={adminDetails?.user_code}
                data-testid="user-code"
                className="text-subTitle"
              >
                {adminDetails?.user_code || '-'} |{' '}
                {adminDetails?.ussd_code || '-'}
              </p>
            </div>
            <div className="flex gap-[24px]">
              {/* Only show View Transactions button for regular Admins (ADM), not Super Admins (SADM) */}
              {adminDetails?.user_code?.startsWith('ADM') && (
                <Button
                  buttonName={'View Transactions'}
                  className={
                    'bg-white border-[1px] border-blue-800 h-fit px-4 py-2'
                  }
                  textSize="text-large"
                  textColor="text-blue-800"
                  buttonOnclick={() =>
                    navigate(
                      `/admin-management/list-of-admins/view-admin/${adminDetails?.id}/transactions`
                    )
                  }
                />
              )}
              <Button
                buttonName={translate(
                  !adminDetails?.status ? 'Activate' : 'Deactivate'
                )}
                buttonOnclick={() =>
                  handelActions(
                    !adminDetails?.status ? 'activate' : 'deactivate'
                  )
                }
                className={`${
                  !adminDetails?.status
                    ? 'bg-positive w-[91px]'
                    : 'bg-negative  w-[110px]'
                } py-2 h-fit px-4 `}
                bgColor={!adminDetails?.status ? 'bg-positive' : 'bg-negative'}
                textSize="text-large"
                testId="status-btn"
              />
              <Button
                buttonName={translate('Update')}
                className="py-2 h-fit px-4 w-[91px]"
                textSize="text-large"
                buttonOnclick={() =>
                  navigate(
                    `/admin-management/view-admin-profile/edit-admin/${id}`
                  )
                }
              />
              <details ref={detailsRef} className="relative">
                <summary className="px-2 py-2 bg-primary-500 text-white rounded-full font-semibold cursor-pointer w-[40px] h-[40px] flex items-center justify-center">
                  <Image
                    src="more-vertical"
                    objectFit={'object-contain'}
                    testId="action-btn"
                  />
                </summary>
                <div className="absolute z-10 bg-white shadow rounded-md w-[170px] mt-1 right-0">
                  <ul className="p-2 border border-s_dark_hover rounded-lg">
                    {resetOptions.map((item) => (
                      <li
                        onClick={() => handelActions(item)}
                        data-testid={`Reset ${item}`}
                        key={item}
                        className="text-primary_text hover:text-p_primary cursor-pointer p-2.5 hover:rounded-lg hover:bg-neutral-100 rounded-xs capitalize"
                      >
                        {translate(`Reset ${item}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            </div>
          </div>
          <div
            className={`flex-grow px-6 pb-4 my-4 h-viewAdminHeight ${getFirefoxCssFix(
              'scroll'
            )} overflow-y-scroll`}
          >
            {Object.keys(information).map((heading, i) => (
              <div key={i} className="mt-6">
                <h1 className="text-heading3 underline underline-offset-4">
                  {notEnglish
                    ? translate(heading, '_')
                    : toTitleCase(heading || '-', '_')}
                </h1>
                <div className="mt-3.5">
                  <div className="-mx-5 flex">
                    {Object.keys(
                      information[heading as keyof AdminDetails]
                    ).map((item, index) => (
                      <div className="px-5 w-1/3" key={index}>
                        <h1 className="text-body2 text-primary_text">
                          {notEnglish
                            ? translate(item, '_')
                            : toTitleCase(item || '-', '_')}
                        </h1>
                        <p
                          data-testid={`${item}`}
                          dir="ltr"
                          title={
                            (
                              information[
                                heading as keyof AdminDetails
                              ] as Record<string, string>
                            )[item]
                          }
                          className={`${
                            item === 'address' && 'text-justify'
                          } text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden ${
                            notEnglish && 'text-right'
                          }`}
                        >
                          {
                            // Type-safe access
                            (
                              information[
                                heading as keyof AdminDetails
                              ] as Record<string, string>
                            )[item] || '-'
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-6">
              <p className="text-heading3 underline underline-offset-4">
                {translate('Uploaded SOP (Standard Operating Procedure)')}
              </p>
              {adminDetails?.sop ? (
                <div className="w-[340px] flex justify-between overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 ">
                  <p>{adminDetails?.sop.split('/').pop()}</p>
                  <Image
                    src="file-eye"
                    className="cursor-pointer "
                    objectFit={'contain'}
                    onClick={() => {
                      const fileUrl =
                        config?.cdnURL + 'public/' + adminDetails?.sop;
                      window.open(fileUrl, '_blank');
                    }}
                  />
                </div>
              ) : (
                <p className="mt-1 font-medium text-gray-900">-</p>
              )}
            </div>

            {/* privileges section starts */}

            {/* tabs section  */}
            <div className="pt-3 sticky -top-4 bg-white">
              <div className="flex items-end">
                {/* Blue Border Section */}
                <p className="bg-white px-2.5 py-2.5 cursor-pointer text-heading4-semi text-p_primary border-b-[2px] border-p_primary">
                  {translate('Access Module')}
                </p>

                {/* Gray Border Section */}
                <div className="flex-1 border-b-[2px] border-[#EEEEEE]"></div>
              </div>
            </div>

            {/* tabs section  */}
            <div className="mt-6">
              {Object.keys(adminDetails?.access_privileges).map(
                (mainPrivilege, index) => (
                  <div key={index} className="border-b border-outline py-2">
                    <h1 className="text-large p-2 mb-1.5">
                      {translate(mainPrivilege)}
                    </h1>
                    <div className="-mx-2.5 flex flex-wrap">
                      {adminDetails?.access_privileges[mainPrivilege].map(
                        (subPrivilege, i) => (
                          <p
                            key={i}
                            className="w-1/3 mb-2 text-body1 text-primary_text px-[18px] py-2 flex-wrap "
                          >
                            {translate(subPrivilege)}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
            {/* privileges section starts */}

            {/* wallet section  */}
            <div className="mt-6 ">
              <p className="text-heading3 underline underline-offset-4">
                Wallet Access
              </p>
              <p className="mt-2 text-body1">
                {adminDetails?.wallet_access?.includes('etop_up')
                  ? 'eTop Up'
                  : '-'}
              </p>
            </div>
            {/* wallet section  */}
          </div>
        </div>
      ) : (
        <Shimmer />
      )}
      {isOpen && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={getModelText().title}
          subText={getModelText().description}
          primaryButtonText={getModelText().buttonName || ''}
          secondaryButtonText={translate('Cancel')}
          handelSubmit={(text) => handleStatus(text)}
          buttonColor={
            selectedAction === 'deactivate'
              ? 'bg-negative'
              : selectedAction === 'activate'
                ? 'bg-positive'
                : 'bg-p_primary'
          }
          textArea={selectedAction === 'deactivate'}
          buttonLoading={buttonLoading}
          buttonWidth={
            (selectedAction === 'password' || selectedAction === 'PIN') &&
            notEnglish
              ? 'w-fit'
              : selectedAction === 'password' || selectedAction === 'PIN'
                ? 'w-[90px]'
                : ''
          }
        />
      )}
    </div>
  );
}
